/**
 * Invite links → app (FR-CON-001). Captures an invite from the URL fragment
 * (`#i=…`), legacy query params (share_target `?text=` / `?url=` / `?invite=`)
 * or a PWA launch queue, parks it in sessionStorage for the Add-contact sheet,
 * and scrubs it from the address bar + history immediately.
 */
import { decodeInviteToken, extractInvite } from "@veto/domain";

export const PENDING_INVITE_KEY = "veto.invitePaste";
const MAX_PENDING = 8192;

function park(text: string): void {
  try {
    sessionStorage.setItem(PENDING_INVITE_KEY, text.slice(0, MAX_PENDING));
  } catch {
    /* private mode — the user can still paste the link */
  }
}

/**
 * Returns true when an invite was found in `href` (default: current location).
 * Rewrites the current URL without it (and without `?addContact=` noise).
 */
export function captureInviteFromLocation(href: string = window.location.href): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  let found: string | null = null;
  if (url.hash && extractInvite(url.hash)) {
    found = url.hash;
    url.hash = "";
  }
  for (const k of ["invite", "text", "title", "url"]) {
    const v = url.searchParams.get(k);
    if (v && extractInvite(v)) {
      found ??= v;
      url.searchParams.delete(k);
    }
  }
  if (!found) return false;
  park(found);
  if (href === window.location.href) {
    const path = url.pathname === "/" ? "/app" : url.pathname;
    const qs = url.searchParams.toString();
    window.history.replaceState(null, "", `${path}${qs ? `?${qs}` : ""}`);
  }
  return true;
}

export function hasPendingInvite(): boolean {
  try {
    return Boolean(sessionStorage.getItem(PENDING_INVITE_KEY));
  } catch {
    return false;
  }
}

/** Display name carried by the parked invite (unverified — for onboarding copy only). */
export function peekPendingInviteName(): string | null {
  try {
    const raw = sessionStorage.getItem(PENDING_INVITE_KEY);
    const ex = raw ? extractInvite(raw) : null;
    if (ex?.kind !== "link") return null;
    return decodeInviteToken(ex.token).name || null;
  } catch {
    return null;
  }
}

type LaunchParams = { targetURL?: string };
type LaunchQueue = { setConsumer: (cb: (p: LaunchParams) => void) => void };

/**
 * `launch_handler: focus-existing` delivers new launches (share target, link
 * capture) to the already-open window through `launchQueue` — without a
 * consumer those invites were silently dropped.
 */
export function listenForLaunchInvites(onInvite: () => void): void {
  const lq = (window as unknown as { launchQueue?: LaunchQueue }).launchQueue;
  if (!lq?.setConsumer) return;
  lq.setConsumer((params) => {
    if (params.targetURL && captureInviteFromLocation(params.targetURL)) onInvite();
  });
}
